import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../ui/dialog";
import { api } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { FileWarning, Plus, Loader2 } from "lucide-react";

const STATUS_STYLES = {
  pending: "text-amber-600 border-amber-500/40 bg-amber-500/10",
  approved: "text-emerald-600 border-emerald-500/40 bg-emerald-500/10",
  denied: "text-red-600 border-red-500/40 bg-red-500/10",
};

export default function PortalExceptions() {
  const { toast } = useToast();
  const [items, setItems] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ policy_id: "", reason: "" });

  const load = () => {
    api.get("/employee-portal/policy-exceptions").then((r) => setItems(r.data.exceptions));
    api.get("/employee-portal/policies").then((r) => setPolicies(r.data.policies));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.policy_id || !form.reason) return;
    setSaving(true);
    try {
      await api.post("/employee-portal/policy-exceptions", form);
      toast({ title: "Exception requested" });
      setOpen(false);
      setForm({ policy_id: "", reason: "" });
      load();
    } finally { setSaving(false); }
  };

  if (!items) return <div className="h-40 rounded-xl bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="portal-exceptions">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Policy Exceptions</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5" disabled={policies.length === 0} data-testid="portal-new-exception-btn"><Plus className="w-3.5 h-3.5" /> Request exception</Button>
          </DialogTrigger>
          <DialogContent data-testid="portal-exception-dialog">
            <DialogHeader><DialogTitle>Request a policy exception</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Policy</Label>
                <Select value={form.policy_id} onValueChange={(v) => setForm({ ...form, policy_id: v })}>
                  <SelectTrigger data-testid="portal-exception-policy-select"><SelectValue placeholder="Choose a policy" /></SelectTrigger>
                  <SelectContent>{policies.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Why do you need an exception?</Label>
                <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} data-testid="portal-exception-reason-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={saving} data-testid="portal-exception-submit-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Submit request"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-2">
        {items.map((v) => (
          <Card key={v.id} data-testid={`portal-exception-row-${v.id}`}>
            <CardContent className="p-4 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <FileWarning className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-[13px] font-medium">{v.policy_title}</div>
                  <div className="text-[12px] text-muted-foreground">{v.reason}</div>
                </div>
              </div>
              <Badge variant="outline" className={`flex-shrink-0 capitalize ${STATUS_STYLES[v.status] || ""}`}>{v.status}</Badge>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-[13px] text-muted-foreground">No exceptions requested yet.</p>}
      </div>
    </div>
  );
}
